// ============================================================================
// TAX RULES — coverage and outcome wording for the Charge Tax Mappings screen
// ============================================================================
// A "tax rule" is one row of property.tbl_charge_tax_mapping: a charge type,
// optionally one item, taxed under a tax master from a date. effectiveTo is
// EXCLUSIVE. A booking line resolves the item's own rule first, then the
// general rule for its charge type (BookingTaxService.ResolveMapping).
//
// Everything here is derived from the property's rules as loaded; the server
// still owns every rule and refuses anything invalid.

import type { Consequence } from 'components/enterprise-shell';
import type { ChargeTaxMapping, ChargeTaxTreatment, ChargeType } from 'api/types/chargeTaxMapping.types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** yyyy-mm-dd (or an ISO timestamp) → "01 Apr 2027". */
export const formatDay = (iso?: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
};

/** yyyy-mm-dd of the day before. */
export const dayBefore = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - 1));
  return dt.toISOString().slice(0, 10);
};

export const todayIso = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const day = (v?: string | null) => (v ? v.slice(0, 10) : '');

/** "room charges" / "meal plan charges" / "add-ons" — or the item's own name. */
export const chargeSubject = (chargeType: ChargeType | '', itemName?: string | null): string => {
  if (itemName) return itemName;
  if (chargeType === 'ROOM') return 'Room charges';
  if (chargeType === 'MEAL') return 'Meal plan charges';
  if (chargeType === 'ADDON') return 'Add-ons';
  return 'These charges';
};

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** The rule in force for (charge, item) on `date`, item-specific only (no fallback). */
const ruleOn = (rules: ChargeTaxMapping[], chargeType: string, itemId: string | null, date: string) =>
  rules.find((r) =>
    r.chargeType === chargeType
    && (r.itemId ?? null) === itemId
    && day(r.effectiveFrom) <= date
    && (!r.effectiveTo || day(r.effectiveTo) > date)) ?? null;

/** The earliest rule for (charge, item) starting on or after `date`. */
const nextRuleFrom = (rules: ChargeTaxMapping[], chargeType: string, itemId: string | null, date: string) =>
  rules
    .filter((r) => r.chargeType === chargeType && (r.itemId ?? null) === itemId && day(r.effectiveFrom) >= date)
    .sort((a, b) => day(a.effectiveFrom).localeCompare(day(b.effectiveFrom)))[0] ?? null;

export interface CoverageGap {
  chargeType: ChargeType;
  /** First day with no general rule. */
  from: string;
  /** Day a later rule starts again; null = no rule at all from `from`. */
  until: string | null;
}

/**
 * The first date, from today, on which each charge type has no GENERAL rule (the rule
 * every item without its own rule falls back to). Item-specific rules do not close a gap
 * — other items of that type would still be refused.
 */
export const findCoverageGaps = (rules: ChargeTaxMapping[], today: string): CoverageGap[] => {
  const gaps: CoverageGap[] = [];
  (['ROOM', 'MEAL', 'ADDON'] as ChargeType[]).forEach((chargeType) => {
    let date = today;
    for (let guard = 0; guard < 100; guard += 1) {
      const rule = ruleOn(rules, chargeType, null, date);
      if (!rule) {
        const next = nextRuleFrom(rules, chargeType, null, date);
        gaps.push({ chargeType, from: date, until: next ? day(next.effectiveFrom) : null });
        return;
      }
      if (!rule.effectiveTo) return;
      date = day(rule.effectiveTo);
    }
  });
  return gaps;
};

const refusalLine = (subject: string, from: string, taxEnabled: boolean): string => (taxEnabled
  ? `Tax calculation is on for this property, so new or edited bookings with ${lower(subject)} checking in from ${formatDay(from)} will be refused.`
  : `Bookings with ${lower(subject)} from ${formatDay(from)} will be refused once tax calculation is switched on for this property.`);

const taxedAs = (treatment: ChargeTaxTreatment, rateLabel: string) =>
  (treatment === 'TAXABLE' ? `taxed under ${rateLabel}` : `${treatment === 'EXEMPT' ? 'exempt' : 'zero-rated'} (no tax)`);

/** The current rule as a noun phrase: "GST 18%" or "being exempt (no tax)". */
const rateOrStatus = (treatment: ChargeTaxTreatment, rateLabel: string) =>
  (treatment === 'TAXABLE' ? rateLabel : `being ${treatment === 'EXEMPT' ? 'exempt' : 'zero-rated'} (no tax)`);

export interface NewRuleInput {
  chargeType: ChargeType | '';
  itemId: string;
  itemName?: string | null;
  rateCode: string;
  taxTreatment: ChargeTaxTreatment;
  effectiveFrom: string;
  sacHsnCode?: string;
}

/** Outcome of adding (or replacing with) a rule. */
export const newRuleConsequence = (
  input: NewRuleInput,
  rules: ChargeTaxMapping[],
  rateLabel: (code: string) => string,
  today: string,
): Consequence | null => {
  if (!input.chargeType || !input.rateCode || !input.effectiveFrom) return null;
  const from = input.effectiveFrom;
  const itemId = input.itemId || null;
  const subject = chargeSubject(input.chargeType, input.itemName);
  const current = rules.find((r) =>
    r.chargeType === input.chargeType && (r.itemId ?? null) === itemId && !r.effectiveTo);

  const lines: string[] = [];
  if (current && current.rateCode === input.rateCode && current.taxTreatment === input.taxTreatment) {
    // Same tax master and status: the rate does not change — say what does, or that nothing does.
    const oldSac = current.sacHsnCode || '';
    const newSac = (input.sacHsnCode || '').trim();
    if (oldSac === newSac) {
      return {
        tone: 'warning',
        lines: [
          `Nothing changes: the new rule uses the same tax master (${rateLabel(current.rateCode)}), tax status and SAC / HSN as the current one.`,
          'Choose a different tax master to change the rate.',
        ],
      };
    }
    lines.push(`${subject} keep ${rateLabel(current.rateCode)}; the rate does not change.`);
    lines.push(`From ${formatDay(from)}, the SAC / HSN printed on tax documents changes from ${oldSac || 'none'} to ${newSac || 'none'}.`);
  } else if (current) {
    lines.push(`${subject} on bookings checking in on or after ${formatDay(from)} will be ${taxedAs(input.taxTreatment, rateLabel(input.rateCode))} instead of ${rateOrStatus(current.taxTreatment, rateLabel(current.rateCode))}.`);
    lines.push(`Check-ins up to ${formatDay(dayBefore(from))} keep ${rateLabel(current.rateCode)}.`);
    lines.push('Existing bookings keep their current tax until they are edited or extended.');
  } else {
    lines.push(`${subject} on bookings checking in on or after ${formatDay(from)} will be ${taxedAs(input.taxTreatment, rateLabel(input.rateCode))}.`);
    if (itemId) {
      lines.push(`This rule takes priority over the general ${lower(chargeSubject(input.chargeType))} rule for this item.`);
    }
  }
  if (from < today) {
    lines.push('The date is in the past: bookings already made are not recalculated until they are edited.');
    return { tone: 'warning', lines };
  }
  return { tone: 'info', lines };
};

/** Outcome of stopping `record` from `endDate` (exclusive) with no successor. */
export const stopRuleConsequence = (
  record: ChargeTaxMapping,
  endDate: string,
  rules: ChargeTaxMapping[],
  rateLabel: (code: string) => string,
  taxEnabled: boolean,
): Consequence | null => {
  if (!endDate) return null;
  const subject = chargeSubject(record.chargeType, record.itemName);
  const itemId = record.itemId ?? null;
  const others = rules.filter((r) => r.id !== record.id);
  const lines = [`${subject} on check-ins up to ${formatDay(dayBefore(endDate))} stay under ${rateLabel(record.rateCode)}.`];

  // A later rule for the same charge and item that starts on the end date takes over.
  const successor = nextRuleFrom(others, record.chargeType, itemId, endDate);
  if (successor && day(successor.effectiveFrom) === endDate) {
    lines.push(`From ${formatDay(endDate)}, the rule already set up for that date applies (${rateLabel(successor.rateCode)}).`);
    return { tone: 'info', lines };
  }

  // An item rule falls back to the general rule for its charge type.
  if (itemId) {
    const general = ruleOn(others, record.chargeType, null, endDate);
    if (general) {
      lines.push(`From ${formatDay(endDate)}, ${record.itemName ?? 'this item'} uses the general ${lower(chargeSubject(record.chargeType))} rule (${rateLabel(general.rateCode)}).`);
      return { tone: 'info', lines };
    }
  }

  lines.push(successor
    ? `From ${formatDay(endDate)} until ${formatDay(dayBefore(day(successor.effectiveFrom)))}, ${lower(subject)} will have no tax rule.`
    : `From ${formatDay(endDate)}, ${lower(subject)} will have no tax rule.`);
  lines.push(refusalLine(subject, endDate, taxEnabled));
  return { tone: taxEnabled ? 'danger' : 'warning', lines };
};

/** Short text + tooltip for the coverage notice in the context bar. */
export const gapNotice = (gaps: CoverageGap[], taxEnabled: boolean, today: string) => {
  if (gaps.length === 0) return null;
  const describe = (g: CoverageGap) => {
    const subject = chargeSubject(g.chargeType);
    const when = g.from <= today ? 'now' : `from ${formatDay(g.from)}`;
    const until = g.until ? ` until ${formatDay(dayBefore(g.until))}` : '';
    return { short: `${subject}: no tax rule ${when}`, long: `${subject} have no tax rule ${when}${until}.` };
  };
  const first = describe(gaps[0]);
  const more = gaps.length > 1 ? ` (+${gaps.length - 1} more)` : '';
  return {
    tone: (taxEnabled && gaps.some((g) => g.from <= today) ? 'danger' : 'warning') as 'danger' | 'warning',
    text: `${first.short}${more}`,
    detail: `${gaps.map((g) => describe(g).long).join(' ')} ${taxEnabled
      ? 'Tax calculation is on for this property, so bookings with these charges on those dates are refused until a rule is added.'
      : 'Bookings with these charges on those dates will be refused once tax calculation is switched on for this property.'}`,
    chargeType: gaps[0].chargeType,
  };
};
